import type { MutableRefObject } from 'react';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { ListChildComponentProps } from 'react-window';
import { FixedSizeList as List } from 'react-window';

import {
    ClickAwayListener,
    Grid,
    IconButton,
    ListItem,
    Paper,
    Popper,
    makeStyles,
} from '@material-ui/core';

import { Search, filterSearchResult } from '@klp/search';

import {
    contextSelectorActions,
    contextSelectors,
} from '../store/contextSelectorSlice';
import type { Bygg } from '@forvalter/types/ForvalterCommonDAL';
import { IconName, Illustration } from '@klp/design/Illustrations';
import {
    useDeleteFavorittMutation,
    useGetByggListQuery,
    usePostFavorittMutation,
} from '../services/rtkApi';
import { useTranslation } from 'react-i18next';
import { Block } from '@klp/design/Block';
import { Tooltip } from '@klp/design/Tooltip';

import useDebounce from '@forvalter/utils/useDebounce';
import { useCurrentUser } from '@klp/auth';
import { showToast } from '@klpkonsern/ui';

/*
enum Direction {
    UP = 'up',
    DOWN = 'down',
}
*/

const useStyles = makeStyles(() => ({
    favoriteBuilding: {
        cursor: 'pointer',
        '&:hover': {
            opacity: 0.7,
        },
    },
}));

type Props = {
    listRef: MutableRefObject<HTMLDivElement | null>;
};
/**
 *
 * @param listRef
 * Pass reference to the jsx element what you want the searchResults to render underneath.
 */
export function ContextSelector({ listRef }: Props) {
    const { t } = useTranslation();
    const classes = useStyles();
    const dispatch = useDispatch();
    const { currentUser } = useCurrentUser();
    const { showByggGrunn, selectorFocus } = useSelector(contextSelectors);
    const { data: byggList } = useGetByggListQuery(
        { includeAll: true },
        {
            skip: !currentUser?.epost,
        },
    );

    const [postFavoritt, { isLoading: postFavorittLoading }] =
        usePostFavorittMutation();

    const [deleteFavoritt, { isLoading: deleteFavorittLoading }] =
        useDeleteFavorittMutation();

    const [openMenu, setOpenMenu] = useState(false);
    const listItemRef = useRef<HTMLDivElement>(null);
    // const [cursor, setCursor] = useState(0);
    const [searchResult, setSearchResult] = useState('');
    const [filteredBygg, setFilteredBygg] = useState(byggList ?? []);
    const { debouncedValue: debouncedSearchResult } = useDebounce(
        searchResult,
        500,
    );

    const { saveSelectedContextToStorage } = contextSelectorActions;

    const filteredByShowByggGrunn = useMemo(() => {
        if (byggList) {
            return showByggGrunn
                ? byggList
                : byggList.filter((bygg) => bygg.isGrunn !== true);
        }
        return [];
    }, [byggList, showByggGrunn]);

    useEffect(() => {
        const filtered = filterSearchResult(
            filteredByShowByggGrunn,
            debouncedSearchResult,
            ['navn', 'id'],
        );
        setFilteredBygg(filtered);
    }, [debouncedSearchResult, filteredByShowByggGrunn]);

    const handleKeyDown = () => {
        listItemRef.current?.focus();
    };

    const handleRemoveSuggestions = () => {
        setSearchResult('');
        setOpenMenu(false);
    };

    const handleSearch = (searchString: string) => {
        setSearchResult(searchString);
        setOpenMenu(true);
    };

    const handleSelect = (bygg: Bygg) => {
        dispatch(saveSelectedContextToStorage(bygg));
        setSearchResult('');
        setOpenMenu(false);
    };

    const handleNyFavoritt = async (byggNummer?: string) => {
        if (!byggNummer) return;
        await postFavoritt({
            byggNummer,
        })
            .unwrap()
            .catch(() => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    const handleDeleteFavoritt = async (favorittId?: number) => {
        if (!favorittId) return;
        await deleteFavoritt({
            favorittId,
        })
            .unwrap()
            .catch(() => {
                showToast({
                    body: t('general.error.message'),
                    type: 'error',
                });
            });
    };

    return (
        <div>
            <Search
                focused={selectorFocus}
                searchResult={searchResult}
                searchHandler={handleSearch}
                searchOnType={true}
                placeholder="general.contextSelectorSearchField"
                keyDownHandler={handleKeyDown}
                // keyDownCallback={(d: Direction) => handleListNavigation(d)}
            />

            <>
                {searchResult !== '' && filteredBygg?.length > 0 && (
                    <ClickAwayListener onClickAway={handleRemoveSuggestions}>
                        <Popper
                            anchorEl={listRef.current}
                            open={
                                searchResult !== '' &&
                                filteredBygg?.length > 0 &&
                                openMenu
                            }
                        >
                            <Paper
                                variant="outlined"
                                elevation={2}
                                style={{
                                    height: '400px',
                                    width: '500px',
                                }}
                            >
                                <List
                                    height={400}
                                    itemCount={filteredBygg?.length}
                                    itemSize={85}
                                    width={500}
                                >
                                    {({
                                        index,
                                        style,
                                    }: ListChildComponentProps) => {
                                        const b = filteredBygg[index];
                                        return (
                                            <ListItem
                                                style={style}
                                                key={b?.id}
                                                ref={
                                                    index === 0
                                                        ? listItemRef
                                                        : undefined
                                                }
                                                dense={true}
                                                button={true}
                                                divider={true}
                                                onClick={() => handleSelect(b)}
                                            >
                                                <Grid
                                                    container={true}
                                                    direction="row"
                                                >
                                                    <Grid item={true}>
                                                        <Block paddingRight="compact">
                                                            <Tooltip
                                                                translation="bygg.favoritt"
                                                                title=""
                                                            >
                                                                <span>
                                                                    <IconButton
                                                                        disabled={
                                                                            postFavorittLoading ||
                                                                            deleteFavorittLoading
                                                                        }
                                                                        onClick={(
                                                                            e,
                                                                        ) => {
                                                                            if (
                                                                                b.erFavoritt
                                                                            ) {
                                                                                handleDeleteFavoritt(
                                                                                    b.favorittId,
                                                                                );
                                                                            } else {
                                                                                handleNyFavoritt(
                                                                                    b.nummer,
                                                                                );
                                                                            }

                                                                            e.stopPropagation();
                                                                        }}
                                                                    >
                                                                        <Illustration
                                                                            className={
                                                                                classes.favoriteBuilding
                                                                            }
                                                                            icon={
                                                                                b?.erFavoritt
                                                                                    ? IconName.STAR_FILLED
                                                                                    : IconName.STAR
                                                                            }
                                                                            size="xsmall"
                                                                            klpPalette="secondary"
                                                                        />
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        </Block>
                                                    </Grid>
                                                    <Grid item={true}>
                                                        <Block paddingTop="tiny">
                                                            {b.navn}
                                                        </Block>
                                                    </Grid>
                                                </Grid>
                                            </ListItem>
                                        );
                                    }}
                                </List>
                            </Paper>
                        </Popper>
                    </ClickAwayListener>
                )}
            </>
        </div>
    );
}
